/* ==========================================================================
   ColorPickerField Component
   Task F34: Color picker with swatch + hex input for intake form
   ========================================================================== */

import { useId } from 'react'

interface ColorPickerFieldProps {
  label: string
  value: string
  onChange: (color: string) => void
  defaultColor?: string
  helperText?: string
  error?: string
}

const DEFAULT_COLOR = '#5C288F'

export function ColorPickerField({
  label,
  value,
  onChange,
  defaultColor = DEFAULT_COLOR,
  helperText,
  error,
}: ColorPickerFieldProps) {
  const id = useId()
  const hasError = Boolean(error)

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value
    // Auto-prefix # if user types hex without it
    if (v && !v.startsWith('#')) {
      v = '#' + v
    }
    onChange(v)
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="flex items-center gap-3">
        {/* Native color picker swatch */}
        <input
          type="color"
          value={value || defaultColor}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="w-10 h-10 rounded border border-gray-300 cursor-pointer p-0.5"
          aria-label={`${label} color picker`}
        />
        {/* Hex text input */}
        <input
          id={id}
          type="text"
          value={value || ''}
          onChange={handleTextChange}
          placeholder={defaultColor}
          maxLength={7}
          className={`
            flex-1 px-3 py-2 text-sm font-mono
            border rounded-md bg-white
            text-charcoal placeholder:text-gray-400
            transition-[border-color,box-shadow] duration-150
            focus:outline-none
            ${hasError
              ? 'border-error focus:border-error focus:shadow-[0_0_0_3px_rgba(198,40,40,0.2)]'
              : 'border-gray-300 focus:border-deep-purple focus:shadow-[0_0_0_3px_rgba(74,53,135,0.15)]'
            }
          `}
          aria-invalid={hasError || undefined}
        />
      </div>
      {(helperText || error) && (
        <p
          className={`text-sm ${hasError ? 'text-error' : 'text-gray-600'}`}
          role={hasError ? 'alert' : undefined}
        >
          {error ?? helperText}
        </p>
      )}
    </div>
  )
}

export default ColorPickerField
